import { preparePageMetadata } from './page-metadata'
import type { Edit } from '@w/crdt'
import { getFractionalIndex } from '../../src/utils/get-fractional-index'
import { ulid } from '@w/utils'
import { practiceClozes, rulePracticeQid } from '@w/content-blocks/src/rule-practice'
import { indexViewBlocks, type BlockLocation, type PageBlock } from './doc-blocks'
import { classifyBlockDelta, type BlockDelta } from './view-delta'

/** Prepare metadata in the same draft, before publishing its Change or any side effects. */
export function prepareQuestionMetadata(tx: Edit): void {
  // An edit whose every op edited text inserts, moves and retypes no block and sets no title, qid or index, so there is
  // nothing to prepare; it skips the two whole-page view indexes below (2.5 ms per keystroke at 2,000 blocks,
  // docs/benchmarks/sync/editor/README.md, Tried).
  if (tx.textOnly) return
  preparePageMetadata(tx)
  const delta = tx.delta()
  if (!delta.movedIds?.length && !changedQuestions(delta.before, delta.after)) return
  const changes = classifyBlockDelta(delta)
  const affected = new Set([
    ...changes.inserted.map(location => location.block.id),
    ...changes.moved.map(change => change.after.block.id),
    ...changes.updated.filter(change => change.before.block.type !== change.after.block.type)
      .map(change => change.after.block.id),
  ])
  const isQuestion = (type: string) => type === 'question' || type === 'cloze'
  // Every question this change writes (inserted, or updated in any field) must carry a qid. An untouched malformed
  // question is tolerated only when the edit skips the ordering scan below; a move, or any edit that runs the scan,
  // still rejects it, as before.
  for (const { block } of [...changes.inserted, ...changes.updated.map(change => change.after)]) {
    if (isQuestion(block.type) && (typeof block.qid !== 'string' || !block.qid))
      throw new Error(`Question block missing qid: ${block.id}`)
  }
  sharePracticeQids(tx, delta.after, changes)
  // Only an affected question takes an index, and a moved block may carry questions; otherwise the loop below writes
  // nothing, so the change region decides and the page's questions are not listed.
  if (changes.moved.length === 0 && !changes.inserted.some(({ block }) => affected.has(block.id) && isQuestion(block.type)) &&
    !changes.updated.some(({ after }) => affected.has(after.block.id) && isQuestion(after.block.type))) return
  const questions = [...indexViewBlocks(delta.after).values()].filter(({ block }) => isQuestion(block.type))
  for (const question of questions) {
    if (typeof question.block.qid !== 'string' || !question.block.qid)
      throw new Error(`Question block missing qid: ${question.block.id}`)
    if (changes.moved.some(change => question.path.startsWith(`${change.after.path}.`)))
      affected.add(question.block.id)
  }
  let before = ''
  for (const [index, location] of questions.entries()) {
    const { block } = location
    if (!affected.has(block.id)) {
      if (typeof block.fractionalIndex === 'string') before = block.fractionalIndex
      continue
    }
    const next = questions.slice(index + 1).find(location => !affected.has(location.block.id))
    const after = typeof next?.block.fractionalIndex === 'string' ? next.block.fractionalIndex : ''
    const current = block.fractionalIndex
    if (before && after && before >= after) { reindex(); return }
    const fractionalIndex = getFractionalIndex(before, after)
    if (!fractionalIndex || fractionalIndex <= before || after && fractionalIndex >= after) {
      reindex()
      return
    }
    if (current !== fractionalIndex) tx.set(location.path, 'fractionalIndex', fractionalIndex)
    before = fractionalIndex
  }

  /** The legacy index alphabet has no nonempty value before "0" or between "A" and "A0". */
  function reindex(): void {
    const width = questions.length.toString(36).length
    for (const [index, location] of questions.entries()) {
      tx.set(location.path, 'fractionalIndex', (index + 1).toString(36).padStart(width, '0'))
    }
  }
}

/**
 * A rule's or question group's `.practice` clozes and questions are one question (rulePracticeQid, as the parser shares
 * them): a practice member this edit inserts, moves or writes takes the qid of the container's unchanged members,
 * keeping its own when it is the first; a member this edit unmarks leaves that question under a fresh qid.
 */
function sharePracticeQids(tx: Edit, after: Parameters<typeof indexViewBlocks>[0], changes: BlockDelta): void {
  const isMember = (block: PageBlock) => block.type === 'cloze' || block.type === 'question'
  const isPractice = (block: PageBlock) => isMember(block) && block.practice === true
  for (const change of changes.updated) {
    if (isMember(change.after.block) && change.before.block.practice === true && change.after.block.practice !== true)
      tx.set(change.after.path, 'qid', ulid())
  }
  const changed = new Set([...changes.inserted, ...changes.updated.map(change => change.after)]
    .filter(({ block }) => isPractice(block)).map(({ block }) => block.id))
  if (changed.size === 0 && changes.moved.length === 0) return
  const index = indexViewBlocks(after)
  if (changes.moved.length > 0) {
    const moved = changes.moved.map(change => change.after.path)
    for (const { block, path } of index.values()) {
      if (isPractice(block) && moved.some(root => path === root || path.startsWith(`${root}.`))) changed.add(block.id)
    }
  }
  const rules = new Map<string, BlockLocation>()
  for (const id of changed) {
    let location = index.get(id)
    while (location && location.block.type !== 'rule' && location.block.type !== 'questionGroup') location = index.get(location.parent)
    if (location) rules.set(location.block.id, location)
  }
  for (const rule of rules.values()) {
    const children = Array.isArray(rule.block.children) ? rule.block.children as readonly PageBlock[] : []
    const qid = rulePracticeQid(children, cloze => !changed.has(cloze.id))
    for (const cloze of practiceClozes(children)) {
      const location = index.get(cloze.id)
      if (qid !== undefined && cloze.qid !== qid && location) tx.set(location.path, 'qid', qid)
    }
  }
}

/** Classifying all inserted prose into location maps cannot affect question metadata. Compare shared view paths
 * first; changed membership is conservative (a moved ancestor can carry an unchanged question subtree). */
function changedQuestions(before: unknown, after: unknown): boolean {
  if (before === after) return false
  const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value)
  const question = (value: unknown): boolean => record(value) && (value.type === 'question' || value.type === 'cloze')
  const contains = (value: unknown): boolean => {
    if (question(value)) return true
    if (!value || typeof value !== 'object') return false
    return Object.values(value).some(child => contains(child))
  }
  if (question(before) || question(after)) return true
  if (Array.isArray(before) && Array.isArray(after)) {
    let start = 0
    let oldEnd = before.length
    let newEnd = after.length
    while (start < oldEnd && start < newEnd && before[start] === after[start]) start++
    while (oldEnd > start && newEnd > start && before[oldEnd - 1] === after[newEnd - 1]) { oldEnd--; newEnd-- }
    for (let index = start; index < oldEnd; index++) if (contains(before[index])) return true
    for (let index = start; index < newEnd; index++) if (contains(after[index])) return true
    return false
  }
  if (!record(before) || !record(after) || before.id !== after.id || before.type !== after.type)
    return contains(before) || contains(after)
  for (const key in before) if (changedQuestions(before[key], after[key])) return true
  for (const key in after) if (!(key in before) && contains(after[key])) return true
  return false
}
